import { useState, useRef, useEffect, useCallback } from 'react'
import { Sparkles, Send, X, FileUp, Paperclip } from 'lucide-react'
import { aiApi, type ProjectProposal, type CreateFromProposalResult } from '../../services/aiApi'
import { ProjectProposalCard, ProposalCreatedMessage } from './ProjectProposalCard'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  attachment?: { fileName: string; fileSize: number }
  proposal?: ProjectProposal
  createdResult?: CreateFromProposalResult
}

interface DocBlock {
  id: string
  filename: string
  content: string
  format: string
}

const docBlockStore: Record<string, DocBlock> = {}

function renderMarkdown(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  // Document blocks: ```document:filename.ext\n...\n```
  html = html.replace(
    /`{3,}document:([^\n]+)\n([\s\S]*?)`{3,}/g,
    (_, filename, content) => {
      const id = 'doc-' + Math.random().toString(36).slice(2, 10)
      const ext = filename.trim().split('.').pop()?.toLowerCase() || 'docx'
      // Unescape HTML entities so content sent to API is clean
      const rawContent = content.trim()
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      docBlockStore[id] = {
        id,
        filename: filename.trim(),
        content: rawContent,
        format: ext,
      }
      const iconMap: Record<string, string> = { xlsx: 'spreadsheet', pptx: 'presentation', docx: 'document', pdf: 'document' }
      const typeLabel = iconMap[ext] || 'document'
      return `<div class="ai-doc-block" data-doc-id="${id}">
        <span class="ai-doc-icon">${typeLabel === 'spreadsheet' ? '&#x1F4CA;' : '&#x1F4C4;'}</span>
        <span class="ai-doc-name">${filename.trim()}</span>
        <button class="ai-doc-download-btn" data-doc-id="${id}">Download</button>
      </div>`
    }
  )

  // Code blocks (remaining)
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // Italic
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  // Unordered lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>')
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
  // Line breaks
  html = html.replace(/\n/g, '<br/>')

  return html
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
]

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AiPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const abortRef = useRef<(() => void) | null>(null)
  const dragCounter = useRef(0)

  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamingContent, scrollToBottom])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handle document download button clicks
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleClick = async (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('[data-doc-id]') as HTMLElement | null
      if (!btn || !btn.classList.contains('ai-doc-download-btn')) return

      const docId = btn.dataset.docId
      if (!docId || !docBlockStore[docId]) return

      const doc = docBlockStore[docId]
      btn.textContent = 'Generating...'
      btn.setAttribute('disabled', 'true')

      try {
        const blob = await aiApi.generateDocument(doc.content, doc.format, doc.filename)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = doc.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        btn.textContent = 'Downloaded'
      } catch (err) {
        console.error('Document generation error:', err)
        btn.textContent = 'Failed'
        setTimeout(() => { btn.textContent = 'Download'; btn.removeAttribute('disabled') }, 2000)
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [isOpen])

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true)
    }
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0

    const file = e.dataTransfer.files[0]
    if (file && ACCEPTED_TYPES.includes(file.type)) {
      setPendingFile(file)
    }
  }

  function handleFileSelect() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf,.docx,.doc,.xlsx,.xls,.txt,.csv'
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) setPendingFile(file)
    }
    input.click()
  }

  async function handleParseDocument(file: File) {
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: `Analyze this document and create a project proposal`,
      timestamp: new Date(),
      attachment: { fileName: file.name, fileSize: file.size },
    }

    setMessages((prev) => [...prev, userMsg])
    setPendingFile(null)
    setInput('')
    setIsParsing(true)

    try {
      const result = await aiApi.parseDocument(file)

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `I've analyzed **${file.name}** and extracted a project proposal. Review the details below and click "Create Project" to set it up.`,
        timestamp: new Date(),
        proposal: result.proposal,
      }
      setMessages((prev) => [...prev, assistantMsg])
    } catch (err) {
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Failed to parse document: ${err instanceof Error ? err.message : 'Unknown error'}`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMsg])
    } finally {
      setIsParsing(false)
    }
  }

  function handleProposalCreated(msgId: string, result: CreateFromProposalResult) {
    // Update the proposal message to remove buttons, add created result
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId ? { ...m, proposal: undefined, createdResult: result } : m
      )
    )

    const successMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      createdResult: result,
    }
    setMessages((prev) => [...prev, successMsg])
  }

  function handleProposalCancel(msgId: string) {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId ? { ...m, proposal: undefined, content: m.content + '\n\n*Proposal cancelled.*' } : m
      )
    )
  }

  function handleSend() {
    const trimmed = input.trim()

    // If there's a pending file, parse it
    if (pendingFile) {
      handleParseDocument(pendingFile)
      return
    }

    if (!trimmed || isStreaming || isParsing) return

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setIsStreaming(true)
    setStreamingContent('')

    const stream = aiApi.chat(trimmed)
    abortRef.current = stream.abort

    let accumulated = ''

    stream.onMessage((text) => {
      accumulated += text
      setStreamingContent(accumulated)
    })

    stream.onDone(() => {
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: accumulated || 'I processed your request but have no additional response.',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, assistantMsg])
      setStreamingContent('')
      setIsStreaming(false)
      abortRef.current = null
    })

    stream.onError((error) => {
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Error: ${error.message}`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMsg])
      setStreamingContent('')
      setIsStreaming(false)
      abortRef.current = null
    })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleClose() {
    if (abortRef.current) {
      abortRef.current()
      abortRef.current = null
    }
    setIsOpen(false)
  }

  const isBusy = isStreaming || isParsing

  return (
    <>
      <button
        className="ai-toggle-btn"
        onClick={() => setIsOpen(true)}
        title="Open CxAI Assistant"
        aria-label="Open CxAI Assistant"
      >
        <Sparkles size={22} />
      </button>

      {isOpen && (
        <div className="ai-panel-overlay" onClick={handleClose}>
          <div
            className="ai-panel ai-panel-open"
            onClick={(e) => e.stopPropagation()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className="ai-drop-overlay">
                <FileUp size={40} />
                <span>Drop document to analyze</span>
                <span className="ai-drop-hint">PDF, DOCX, XLSX, or text files</span>
              </div>
            )}

            <div className="ai-panel-header">
              <div className="ai-panel-title">
                <Sparkles size={18} />
                <span>CxAI Assistant</span>
              </div>
              <button className="ai-panel-close" onClick={handleClose} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="ai-messages" ref={messagesContainerRef}>
              {messages.length === 0 && !isBusy && (
                <div className="ai-welcome">
                  <Sparkles size={32} />
                  <h3>CxAI Assistant</h3>
                  <p>
                    Ask me about commissioning processes, equipment specs, checklist items,
                    or anything related to your projects.
                  </p>
                  <p className="ai-welcome-hint">
                    <FileUp size={16} /> Drop a fee proposal or project doc to auto-create a project
                  </p>
                </div>
              )}

              {messages.map((msg) => (
                <div key={msg.id} className={`ai-message ai-message-${msg.role}`}>
                  {msg.attachment && (
                    <div className="ai-attachment-chip">
                      <Paperclip size={14} />
                      <span>{msg.attachment.fileName}</span>
                      <span className="ai-attachment-size">{formatFileSize(msg.attachment.fileSize)}</span>
                    </div>
                  )}
                  <div
                    className="ai-message-bubble"
                    dangerouslySetInnerHTML={{
                      __html: msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content,
                    }}
                  />
                  {msg.proposal && (
                    <ProjectProposalCard
                      proposal={msg.proposal}
                      onCreated={(result) => handleProposalCreated(msg.id, result)}
                      onCancel={() => handleProposalCancel(msg.id)}
                    />
                  )}
                  {msg.createdResult && (
                    <ProposalCreatedMessage result={msg.createdResult} />
                  )}
                </div>
              ))}

              {isParsing && (
                <div className="ai-message ai-message-assistant">
                  <div className="ai-message-bubble">
                    <span className="ai-parsing-indicator">
                      <FileUp size={16} className="spin" />
                      Analyzing document...
                    </span>
                  </div>
                </div>
              )}

              {isStreaming && (
                <div className="ai-message ai-message-assistant">
                  <div className="ai-message-bubble">
                    {streamingContent ? (
                      <span dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingContent) }} />
                    ) : (
                      <span className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="ai-input-area">
              {pendingFile && (
                <div className="ai-pending-file">
                  <Paperclip size={14} />
                  <span className="ai-pending-file-name">{pendingFile.name}</span>
                  <span className="ai-pending-file-size">{formatFileSize(pendingFile.size)}</span>
                  <button
                    className="ai-pending-file-remove"
                    onClick={() => setPendingFile(null)}
                    aria-label="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <div className="ai-input-row">
                <button
                  className="ai-attach-btn"
                  onClick={handleFileSelect}
                  disabled={isBusy}
                  title="Attach document"
                  aria-label="Attach document"
                >
                  <Paperclip size={18} />
                </button>
                <textarea
                  ref={inputRef}
                  className="ai-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={pendingFile ? 'Press Enter to analyze document...' : 'Ask CxAI something...'}
                  rows={1}
                  disabled={isBusy}
                />
                <button
                  className="ai-send-btn"
                  onClick={handleSend}
                  disabled={(!input.trim() && !pendingFile) || isBusy}
                  aria-label="Send message"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
